import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';

import { tpoGenKeys } from '../../utils/tpoGenQueryKeys';
import { customerConfigSchema, ConfigValidationError } from '../../config/schema/customerConfigSchema';
import type { CustomerConfigResponse } from '../../types/config';
import { parsePromoConfigResponse } from '../../config/parsePromoConfig';
import type { ParsedPromoOptions } from '../../config/parsePromoConfig';
import { adaptKpiCatalog } from '../../config/adapters/kpiCatalogAdapter';
import { parseTpoGenConfig } from '../../config/adapters/parseTpoGenConfig';
import type { TpoGenConfig } from '../../config/adapters/parseTpoGenConfig';
import type { KpiOption } from '../../PlanDetail/MetricPage/config/kpiCatalog';
import { shouldUseFallbackConfig } from '../../config/fallback/shouldUseFallbackConfig';

export interface ParsedCustomerConfig {
    /** Raw, schema-validated backend response. */
    raw: CustomerConfigResponse;
    /** Drop-in replacement for the old static tpoGenConfig object. */
    tpoGenConfig: TpoGenConfig;
    /** Promo type + dependent tactic dropdown options. */
    parsedPromoOptions: ParsedPromoOptions;
    /** Report KPI catalog (Plan Metric view). */
    kpiCatalog: KpiOption[];
}

const fetchRawConfig = async (signal?: AbortSignal): Promise<unknown> => {
    if (shouldUseFallbackConfig()) {
        // Dynamic import keeps the fixture out of the production bundle.
        // const mod = await import('../../config/fallback/fallbackCustomerConfig.json');
        // return mod.default;
        const [fixtureMod, feConfigMod] = await Promise.all([
            import('../../config/fallback/fallbackCustomerConfig.json'),
            import('../../config/tpoGenConfig'),
        ]);
        return { ...fixtureMod.default, fe_config: feConfigMod.default };
    }
    const token = localStorage.getItem('accessToken');
    // const response = await axios(tpoGenUrls.getConfig, {
    //     method: 'GET',
    //     headers: { Authorization: `Bearer ${token}` },
    //     signal,
    // });
    // return response.data;
    const [response, feConfigMod] = await Promise.all([
        axios(tpoGenUrls.getConfig, {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
            signal,
        }),
        import('../../config/tpoGenConfig'),
    ]);
    return { ...response.data, fe_config: feConfigMod.default };
};

const loadParsedConfig = async (signal?: AbortSignal): Promise<ParsedCustomerConfig> => {
    const rawUnknown = await fetchRawConfig(signal);

    const result = customerConfigSchema.safeParse(rawUnknown);
    if (!result.success) {
        throw new ConfigValidationError(
            result.error.issues.map((issue) => ({ path: issue.path, message: issue.message })),
        );
    }
    const raw = result.data;

    // Adapters run here (not in render) so referential-integrity throws surface
    // as query errors handled by the gate, not the global ErrorBoundary.
    const tpoGenConfig = parseTpoGenConfig(raw);
    const parsedPromoOptions = parsePromoConfigResponse(raw);
    const kpiCatalog = adaptKpiCatalog(raw.metrics);

    return { raw, tpoGenConfig, parsedPromoOptions, kpiCatalog };
};

/**
 * Fetches + validates + parses the customer config once per session.
 * `staleTime`/`gcTime: Infinity` (the global defaults are 1h) keep it cached for
 * the whole SPA session; `retry: false` so a validation error fails fast to the
 * gate instead of retrying.
 */
export function useCustomerConfigQuery() {
    return useQuery({
        queryKey: tpoGenKeys.config(),
        queryFn: ({ signal }) => loadParsedConfig(signal),
        staleTime: Infinity,
        gcTime: Infinity,
        retry: false,
    });
}
